// Settings → Sign-in methods (owner decision 2026-10-19): your email (always works), Discord and GitHub to link or
// unlink, and passkeys to add, rename and remove. Email can't be removed, so there's always a way back in.

import { ApiError, api } from "../api.ts";
import { clear, errorBox, fmtDate, h, toast } from "../dom.ts";
import { addPasskey, deviceName, finishProvider, getSigninMethods, passkeysSupported, PROVIDER_NAME, startProvider, type Passkey, type Provider, type SigninMethods } from "./me-methods.ts";

const LINK_ERRORS: Record<string, string> = {
  identity_taken: "That account already signs in to another Banger account. Sign in there and unlink it first.",
  provider_linked: "You already have one linked. Unlink it first to use a different one.",
  wrong_account: "That link was started from another Banger account.",
  code_invalid: "That took too long or was already used. Please try again.",
};

/** The section; `oauth` is the code a link round trip came back with (`/me/settings?oauth=`). */
export function signinMethodsSection(oauth: string | null, oauthError: string | null): HTMLElement {
  const body = h("div", null, h("p", { class: "dim" }, "Loading…"));
  const note = h("div", { "aria-live": "polite" });
  const paint = async () => {
    let m: SigninMethods;
    try {
      m = await getSigninMethods();
    } catch (e) {
      return clear(body, errorBox(e));
    }
    clear(body, rows(m, paint, note));
  };
  void (async () => {
    if (oauth) {
      try {
        const r = await finishProvider(oauth);
        if (r.kind === "linked") toast(`${PROVIDER_NAME[r.provider]} is linked`);
      } catch (e) {
        const code = e instanceof ApiError ? e.code : (e as { code?: string }).code;
        clear(note, h("p", { class: "error", role: "alert" }, LINK_ERRORS[code ?? ""] ?? (e instanceof Error ? e.message : "That didn't work.")));
      }
    } else if (oauthError) {
      clear(note, h("p", { class: "error", role: "alert" }, oauthError === "denied" ? "You cancelled at the provider; nothing was linked." : "That didn't work; nothing was linked. Please try again."));
    }
    await paint();
  })();
  return h(
    "section",
    { id: "signin-methods", class: "set-card" },
    h("h2", null, "Sign-in methods"),
    h("p", { class: "dim" }, "The ways you can sign in. Your email always works: we send you a link."),
    note,
    body,
  );
}

const row = (title: string, text: string | null, control: HTMLElement | null) =>
  h("div", { class: "set-row" }, h("div", null, h("b", null, title), text ? h("p", null, text) : null), control);

function rows(m: SigninMethods, repaint: () => Promise<void>, note: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [row("Email", m.email ? `${m.email}. We send a sign-in link; no password.` : "We send a sign-in link; no password.", h("span", { class: "dim small" }, "Always on"))];
  for (const p of ["discord", "github"] as Provider[]) {
    const linked = m.identities.find((i) => i.provider === p);
    if (!linked && !m.available[p]) continue;
    const btn = h("button", { class: "btn small ghost", type: "button" }, linked ? "Unlink" : "Link");
    btn.addEventListener("click", async () => {
      btn.setAttribute("disabled", "");
      clear(note);
      try {
        if (linked) {
          await api("accounts", `/me/identities/${p}`, { method: "DELETE" });
          toast(`${PROVIDER_NAME[p]} is unlinked`);
          await repaint();
        } else await startProvider(p, { link: true, next: "/me/settings#signin-methods" });
      } catch (e) {
        btn.removeAttribute("disabled");
        clear(note, errorBox(e));
      }
    });
    out.push(row(PROVIDER_NAME[p], linked ? `Linked${linked.email ? ` (${linked.email})` : ""} since ${fmtDate(linked.createdAt)}.` : `Sign in with your ${PROVIDER_NAME[p]} account.`, btn));
  }
  if (m.available.passkey || m.passkeys.length) {
    for (const k of m.passkeys) out.push(passkeyRow(k, repaint, note));
    if (m.available.passkey) {
      const supported = passkeysSupported();
      const add = h("button", { class: "btn small", type: "button", disabled: !supported }, "Add a passkey");
      add.addEventListener("click", async () => {
        add.setAttribute("disabled", "");
        clear(note);
        try {
          const k = await addPasskey(deviceName());
          if (k) toast(`Passkey added: ${k.name}`);
          await repaint();
        } catch (e) {
          add.removeAttribute("disabled");
          clear(note, errorBox(e));
        }
      });
      out.push(
        row(
          m.passkeys.length ? "Another passkey" : "Passkeys",
          supported ? "Sign in with your fingerprint, face or device PIN instead of an email link. Add one on each device you use." : "This browser can't make passkeys. Try Safari, Chrome, Edge or Firefox on a recent system.",
          add,
        ),
      );
    }
  }
  return out;
}

function passkeyRow(k: Passkey, repaint: () => Promise<void>, note: HTMLElement): HTMLElement {
  const rename = h("button", { class: "btn small ghost", type: "button" }, "Rename");
  const remove = h("button", { class: "btn small ghost", type: "button" }, "Remove");
  rename.addEventListener("click", async () => {
    const name = prompt("Name this passkey", k.name)?.trim();
    if (!name || name === k.name) return;
    try {
      await api("accounts", `/me/passkeys/${encodeURIComponent(k.id)}`, { method: "PATCH", body: { name } });
      await repaint();
    } catch (e) {
      clear(note, errorBox(e));
    }
  });
  remove.addEventListener("click", async () => {
    if (!confirm(`Remove the passkey "${k.name}"? You can still sign in with your email.`)) return;
    try {
      await api("accounts", `/me/passkeys/${encodeURIComponent(k.id)}`, { method: "DELETE" });
      toast("Passkey removed");
      await repaint();
    } catch (e) {
      clear(note, errorBox(e));
    }
  });
  const used = k.lastUsedAt ? `last used ${fmtDate(k.lastUsedAt)}` : "not used yet";
  return row(`Passkey: ${k.name}`, `Added ${fmtDate(k.createdAt)}, ${used}.`, h("div", { class: "acct-row", style: { marginTop: "0" } }, rename, remove));
}
